// Where a member stands on their accountability (Adam, 18 Oct, 15:45): "They should only be on track if they turn up
// for every session and they do all of the actions. Then there should be different levels of behind." Worked out in
// the front end from what they've slipped on, never from the database's guarantee state, which keeps its own rules in
// Masterminds. Pure: no imports.
//
// A slip is a session not attended live (missed, even if they caught up on the recording) or an action not done (part
// done, or carried over and still open, counts as not done; carried over and then done counts as done late, not a
// slip: Adam, 25 Oct). Change a level's most slips here, in 1 place.
//
// Adam can set a member's level by hand over the automatic one (his answer to question 2, 20 Oct; migration 80): the
// member's log carries the level he set and the summary its level_set, and withSet below puts it over the automatic
// level, in 1 place, for the member's pages and Masterminds alike. The automatic level is still worked out only here.
export const LEVELS = [
  { key: 'on_track', most: 0 },
  { key: 'a_little_behind', most: 2 },
  { key: 'behind', most: 5 },
  { key: 'well_behind', most: Infinity },
]
export const LEVEL_KEYS = LEVELS.map(l => l.key)
export const BEHIND = ['a_little_behind', 'behind', 'well_behind']
export const isBehind = key => BEHIND.includes(key)

// { held, live, missed, caught, notDone, due } -> { key, slips, sessionsMissed, caught, notDone, held, live }
// held: sessions held in their term that count (not excused, paused or without a register); live: attended live;
// missed: missed with the recording not watched; caught: missed and caught up on the recording, on time or late;
// notDone: actions not done, part done or carried over; due: actions that have fallen due.
// Getting started only before any session is held or any action falls due. Never attended live while sessions have
// been held is Well behind, whatever the count.
export function levelOf({ held = 0, live = 0, missed = 0, caught = 0, notDone = 0, due = 0 } = {}) {
  const sessionsMissed = missed + caught
  const slips = sessionsMissed + notDone
  const facts = { slips, sessionsMissed, caught, notDone, held, live, due }
  if (!held && !due) return { key: 'getting_started', ...facts }
  if (held && !live) return { key: 'well_behind', ...facts, never: true }
  const level = LEVELS.find(l => slips <= l.most) || LEVELS[LEVELS.length - 1]
  return { key: level.key, ...facts }
}

// From the log's own counts (accountability in log.js)
export const levelFromLog = acc => levelOf({
  held: acc.sessions.counted, live: acc.sessions.live, missed: acc.sessions.missed, caught: acc.sessions.caught + acc.sessions.late,
  notDone: acc.actions.notDone, due: acc.actions.due,
})

// From the summary hub.mastermind_members gives each member (the list, a member's head and the person card), which
// counts each action once, by how it ended: done late (carried first), part done, not done, or carried and still open.
// The summary's missed already holds the sessions caught up on too late (caught_up_late is a part of it, migration
// 20261013210000's mm_log_summary), so they come out of missed and count once, as caught, as the log counts them.
export function levelFromSummary(sum) {
  const se = sum?.sessions || {}
  const ac = sum?.actions || {}
  const late = se.caught_up_late || 0
  // Adam, 25 Oct: done late (carried, then done) is done, not a slip
  const notDone = (ac.part_done || 0) + (ac.not_done || 0) + (ac.carried_open || 0)
  return levelOf({
    held: se.counted || 0, live: se.live || 0, missed: Math.max(0, (se.missed || 0) - late), caught: (se.caught_up || 0) + late,
    notDone, due: (ac.finished || 0) + (ac.carried_open || 0),
  })
}

// The log's own counts in the summary, once the database gives them (summary.log, the build plan's migration 80, which
// mirrors accountability() in log.js: the totals of actions and sessions, in the log's names or the database's), or
// null before then
export function countsFromSummary(sum) {
  const t = sum?.log?.totals
  if (!t || typeof t !== 'object') return null
  const a = t.actions || {}
  const s = t.sessions || {}
  const n = (o, ...keys) => { for (const k of keys) if (Number.isFinite(o[k])) return o[k]; return 0 }
  return {
    actions: { due: n(a, 'due'), done: n(a, 'done'), notDone: n(a, 'notDone', 'not_done') },
    sessions: { counted: n(s, 'counted'), live: n(s, 'live'), caught: n(s, 'caught'), late: n(s, 'late'), missed: n(s, 'missed') },
  }
}

// Where they stand from the summary: the log's own counts when the summary has them, so the list, the head and the
// person card count as the member's log does, else the summary's own (levelFromSummary), with the level Adam set by
// hand over it (the summary's level_set, migration 80)
export function levelFromCounts(sum) {
  const c = countsFromSummary(sum)
  return withSet(c ? levelFromLog(c) : levelFromSummary(sum), sum?.level_set)
}

// The levels as bands of slips, for the legend over the members: [{ key, from, to }], to Infinity for the last
export function levelBands() {
  let from = 0
  return LEVELS.map(l => {
    const band = { key: l.key, from, to: l.most }
    from = l.most + 1
    return band
  })
}

// The level Adam set by hand over the automatic one (migration 80: a member's log's level, a summary's level_set), or
// the automatic level, the same object, when none is set. set: a level's key, or { level }. The facts (slips, sessions
// missed, actions not done) stay the automatic ones: the record is the record. auto: the automatic level's key. fits:
// those slips fall in the set level's own band (levelBands), so words that name a count still hold; when they don't,
// the count-free words are used (logWords.js LOG.pushSet, REFOCUS.headingSet).
export function withSet(auto, set) {
  const key = typeof set === 'string' ? set : set?.level
  if (!LEVEL_KEYS.includes(key)) return auto
  const band = levelBands().find(b => b.key === key)
  const fits = key === auto.key
    || (!auto.never && auto.key !== 'getting_started' && auto.slips >= band.from && auto.slips <= band.to)
  return { ...auto, key, auto: auto.key, set: true, fits }
}
